import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageConfigProvider } from '../../contexts/PageConfigContext';
import AlertModal from '../modals/AlertModal';

const ProtectedRoute = ({ children, resource }) => {
  const { isAuthenticated, loading, user, token, logout, availableUgp, loadingAvailableUgp, loginDisabled, loginDisabledMessage, currentRole, loadingRole, mainPlatformAccess, loadingMainPlatformAccess } = useAuth();
  const location = useLocation();
  const [pageConfigState, setPageConfigState] = useState({ loading: false, hasAccess: null, configuration: null });

  // Fetch page config + access when resource is set (single query, deny by default)
  // Use AbortController to cancel in-flight fetches when deps change - prevents stale
  // "Access Denied" flash from an outdated response (e.g. before currentRole loaded)
  useEffect(() => {
    if (!resource || loadingRole || !user?.id || !token) {
      // Always reset when resource is set and we bail - prevents stale hasAccess from a different page
      if (resource) {
        setPageConfigState({ loading: true, hasAccess: null, configuration: null });
      }
      return;
    }
    const controller = new AbortController();
    const params = new URLSearchParams({ userId: user.id });
    if (currentRole?.groups_periods_id) params.append('groups_periods_id', currentRole.groups_periods_id);
    if (currentRole?.group_name) params.append('group_name', currentRole.group_name);
    if (currentRole?.relationship_type) params.append('relationship_type', currentRole.relationship_type);
    if (currentRole?.parent_group_name) params.append('parent_group_name', currentRole.parent_group_name);
    const typeParam = new URLSearchParams(location.search).get('type');
    if (resource === 'groups' && typeParam) params.append('type', typeParam);

    setPageConfigState({ loading: true, hasAccess: null, configuration: null });
    fetch(`/api/page-configurations/page/${resource}?${params}`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: controller.signal
    })
      .then(res => res.json())
      .then(data => {
        if (!data.success) {
          setPageConfigState({ loading: false, hasAccess: false, configuration: {} });
          return;
        }
        const hasAccess = data.data?.hasAccess === true;
        const configuration = data.data?.configuration || {};
        setPageConfigState({ loading: false, hasAccess, configuration });
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setPageConfigState({ loading: false, hasAccess: false, configuration: {} });
      });

    return () => controller.abort();
  }, [resource, user?.id, token, loadingRole, currentRole?.groups_periods_id, currentRole?.group_name, currentRole?.relationship_type, currentRole?.parent_group_name, location.search]);

  // Redirect to auth as soon as we know user is not authenticated (don't wait for org roles or page config)
  if (!loading && !isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: { pathname: location.pathname, search: location.search, hash: location.hash } }} />;
  }

  // Show loading while auth is being checked, or (if authenticated) while org roles / page config are loading
  const isLoadingConfig = resource && (pageConfigState.loading || pageConfigState.hasAccess === null);
  const waitingForShell = isAuthenticated && (loadingMainPlatformAccess || mainPlatformAccess == null);
  if (loading || loadingAvailableUgp || waitingForShell || isLoadingConfig) {
    return (
      <div style={styles.loadingContainer}>
        <div className="loading-spinner loading-spinner--sm" />
      </div>
    );
  }

  // CRITICAL: Block access if user's login is disabled - applies to ALL users including Super - Admin
  // Check both loginDisabled state from AuthContext AND user.canLogin field as double verification
  if (loginDisabled || (user && user.canLogin === false)) {
    return (
      <div style={styles.blockingContainer}>
        <AlertModal
          isOpen={true}
          onClose={() => {
            // Don't allow closing - user must logout
          }}
          variant="error"
          subject="Access Denied"
          body={loginDisabledMessage || 'Your account login has been disabled. Please contact support or logout.'}
          icon="🚫"
          showCloseButton={false}
          allowOverlayClose={false}
          autoClose={false}
          actions={[
            {
              label: 'Logout',
              onClick: () => logout(),
              variant: 'primary',
              style: { background: '#dc3545' }
            }
          ]}
          zIndex={9999}
        />
      </div>
    );
  }

  if (mainPlatformAccess === false) {
    return <Navigate to="/" replace />;
  }

  // CRITICAL: Block access if user has no organization roles - page never renders
  // Uses data from AuthContext (shared with Navigation) to avoid duplicate queries
  // Note: Super - Admin users still need organization roles for protected pages
  if (!loadingAvailableUgp && (!availableUgp || availableUgp.length === 0)) {
    return (
      <div style={styles.blockingContainer}>
        <AlertModal
          isOpen={true}
          onClose={() => {
            // Don't allow closing - user must contact support or logout
          }}
          variant="error"
          subject="Access Restricted"
          body="Contact support--failed to load your role. You do not have access to any organization roles."
          icon="⚠️"
          showCloseButton={false}
          allowOverlayClose={false}
          autoClose={false}
          actions={[
            {
              label: 'Logout',
              onClick: () => logout(),
              variant: 'primary',
              style: { background: '#dc3545' }
            }
          ]}
          zIndex={9999}
        />
      </div>
    );
  }

  // When resource is set: deny by default - require explicit permission
  if (resource && pageConfigState.hasAccess === false) {
    return (
      <div style={styles.blockingContainer}>
        <AlertModal
          isOpen={true}
          onClose={() => {}}
          variant="error"
          subject="Access Denied"
          body="You do not have permission to access this page."
          icon="🚫"
          showCloseButton={false}
          allowOverlayClose={false}
          autoClose={false}
          actions={[
            { label: 'Go to Home', onClick: () => window.location.href = '/', variant: 'primary' }
          ]}
          zIndex={9999}
        />
      </div>
    );
  }

  // All checks passed - render the protected page (with optional page config from context)
  const pageConfigValue = resource ? { configuration: pageConfigState.configuration || {} } : null;
  if (pageConfigValue) {
    return (
      <PageConfigProvider value={pageConfigValue}>
        {children}
      </PageConfigProvider>
    );
  }
  return children;
};

const styles = {
  loadingContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255,255,255,1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000
  },
  blockingContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 9998
  }
};

export default ProtectedRoute;